import { useState } from 'react';
import type { MintedKey } from '@xinfer/api-client';

/**
 * The one-time key reveal.
 *
 * This is the only moment the plaintext exists anywhere outside the user's
 * clipboard, so it is a hard modal: no toast, no auto-dismiss, and no way out
 * except an explicit acknowledgement. Getting this wrong is a support burden
 * forever, because the answer to "I lost my key" is always "mint a new one".
 */
export function KeyRevealModal({ minted, onClose }: { minted: MintedKey; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(minted.key);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the key is on screen and selectable,
      // so this is not worth an error state.
      setCopied(false);
    }
  }

  return (
    <div className="backdrop" role="dialog" aria-modal="true" aria-labelledby="reveal-title">
      <div className="modal">
        <h2 id="reveal-title">Your new API key</h2>
        <p className="warn">
          This is the only time this key is shown. It is not stored anywhere we can read it,
          so if you lose it you will have to create another.
        </p>

        <div className="key-box mono" data-testid="plaintext-key">
          {minted.key}
        </div>

        <div className="row">
          <button className="ghost" onClick={copy}>
            {copied ? 'Copied' : 'Copy to clipboard'}
          </button>
          <span className="muted">ends in {minted.hint}</span>
        </div>

        <label className="row" style={{ marginTop: 18 }}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span>I have stored this key somewhere safe</span>
        </label>

        <div className="row" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
          <button className="primary" disabled={!acknowledged} onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
